/**
 * NearMeStateCard — iter319 / iter322 premium card for the Home
 * "Near You" tab.
 *
 * Five message states (A→E) collapse into a single bilingual card:
 *   A. Searching      — locationStatus === 'requesting'
 *   B. Nearby found   — hasNearbyMatch === true
 *   C. No nearby      — hasLocation && !hasNearbyMatch
 *   D. Permission off — locationStatus === 'denied' / 'unsupported'
 *   E. Read failed    — locationStatus === 'error'
 *
 * iter322 — Replaced the legacy single-country `<select>` with the
 * multi-country `<LocationPickerSheet>` (Country → Region → City →
 * Area + search + GPS confirmation). The card now shows the
 * resolved location label next to the "Choose your location" button
 * so the operator can see at a glance what's currently filtering.
 *
 * The buyer can also clear the location and fall back to the
 * canonical live-ads feed. Manual fallback is critical in Sudan
 * where browser geolocation can be unreliable.
 */

import React from 'react';
import { MapPin, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import LocationPickerSheet from './LocationPickerSheet';
import { formatLocationLabel } from '../data/locations';

// ── Legacy export (kept so any other callers don't break) ──────────
// Some tests / cards still consume the simple Sudanese city slug list.
// We leave it in place but the active UI no longer references it.
export const NEAR_ME_CITY_OPTIONS = [
  { key: 'Khartoum',       ar: 'الخرطوم',     en: 'Khartoum' },
  { key: 'Omdurman',       ar: 'أم درمان',    en: 'Omdurman' },
  { key: 'Khartoum North', ar: 'بحري',        en: 'Khartoum North (Bahri)' },
  { key: 'Port Sudan',     ar: 'بورتسودان',   en: 'Port Sudan' },
  { key: 'Wad Madani',     ar: 'ود مدني',     en: 'Wad Madani' },
];

/**
 * Props:
 *   t                  i18n translate fn
 *   isRTL              bool
 *   isGuest            bool — true when no user is signed in. iter324 — used to
 *                      separate the welcoming idle state (Sign in CTA shown) from
 *                      the misleading "Location access is off" denial copy that
 *                      a guest must NEVER see before tapping Use my location.
 *   locationStatus     'idle' | 'requesting' | 'granted' | 'denied' | 'unsupported' | 'error'
 *   nearMeResult       { hasLocation, hasNearbyMatch, matchRadiusKm, nearbyCount, locationFilterMode, strictManualEmpty, ... }
 *   location           { country, region, city, area, latitude, longitude, locationConfirmed, locationLabel } | null
 *   pickerOpen         bool — controls the LocationPickerSheet visibility
 *   onRequestLocation  () => void — GPS "Use my location"
 *   onOpenPicker       () => void — opens the picker sheet
 *   onClosePicker      () => void
 *   onApplyLocation    (loc) => void — buyer-confirmed selection
 *   onClearLocation    () => void
 *   onSignIn           () => void — iter324 — navigates to /auth?mode=login. Guest only.
 */
export default function NearMeStateCard({
  t,
  isRTL,
  isGuest = false,
  locationStatus,
  nearMeResult,
  location,
  pickerOpen,
  onRequestLocation,
  onOpenPicker,
  onClosePicker,
  onApplyLocation,
  onClearLocation,
  onSignIn,
}) {
  const { hasLocation, hasNearbyMatch, matchRadiusKm, nearbyCount, locationFilterMode, strictManualEmpty } = nearMeResult || {};
  const isSearching = locationStatus === 'requesting';
  // iter324 — STRICT split: `denied` is the only state that should
  // surface the permission-denied copy. `unsupported` (no navigator
  // .geolocation) gets its own honest message because the user can't
  // "turn on" something the browser doesn't ship. `idle` (never asked)
  // is treated as the welcoming starter state regardless of auth.
  const isDenied = locationStatus === 'denied';
  const isUnsupported = locationStatus === 'unsupported';
  const isIdle = locationStatus === 'idle' || locationStatus == null || locationStatus === '';
  const isError = locationStatus === 'error';
  const isManualStrict = locationFilterMode === 'manual-strict';

  const hasManualSelection = Boolean(location && (location.country || location.city || location.area));
  const manualLabel = hasManualSelection
    ? formatLocationLabel(location, isRTL ? 'ar' : 'en')
    : '';

  const Picker = (
    <LocationPickerSheet
      open={!!pickerOpen}
      value={location || null}
      onClose={onClosePicker}
      onApply={(loc) => { if (typeof onApplyLocation === 'function') onApplyLocation(loc); }}
      onClear={() => { if (typeof onClearLocation === 'function') onClearLocation(); }}
      testid="near-me-location-picker"
    />
  );

  // iter322 Phase A refinement — Strict manual location filter is set
  // but yielded ZERO matching ads. Show the premium empty state with
  // "Change location" + "Clear location" CTAs. This is the canonical
  // "No ads found in this location yet" UX requested in iter322.
  if (isManualStrict && strictManualEmpty) {
    return (
      <>
        <div
          className="near-me-card near-me-card--neutral"
          data-testid="near-me-state-card"
          data-state="manual-strict-empty"
          data-filter-mode="manual-strict"
          role="note"
        >
          <span className="near-me-card__icon" aria-hidden="true"><MapPin size={18} /></span>
          <span className="near-me-card__msg" data-testid="near-me-strict-empty-title">
            {t('home.nearMe.emptyManualTitle')}
          </span>
          {manualLabel && (
            <span
              className="near-me-card__chosen"
              data-testid="near-me-location-chip"
              style={{ flex: '1 1 100%', fontSize: 12, opacity: 0.85, wordBreak: 'break-word' }}
            >
              {t('locationSelector.chosen')} <strong>{manualLabel}</strong>
            </span>
          )}
          <span
            className="near-me-card__hint"
            data-testid="near-me-strict-empty-hint"
            style={{ flex: '1 1 100%', fontSize: 12, opacity: 0.75 }}
          >
            {t('home.nearMe.emptyManualHint')}
          </span>
          <div className="near-me-card__actions">
            <button
              type="button"
              className="near-me-card__cta"
              data-testid="change-location-btn"
              onClick={onOpenPicker}
              aria-expanded={!!pickerOpen}
              aria-controls="near-me-location-picker"
            >
              <MapPin size={13} />
              <span>{t('home.nearMe.changeLocation')}</span>
            </button>
            <button
              type="button"
              className="near-me-card__cta near-me-card__cta--ghost"
              data-testid="clear-location-btn"
              onClick={onClearLocation}
            >
              <span>{t('home.nearMe.clearLocation')}</span>
            </button>
          </div>
        </div>
        {Picker}
      </>
    );
  }

  // State A — actively searching for GPS.
  if (isSearching) {
    return (
      <>
        <div
          className="near-me-card near-me-card--loading"
          data-testid="near-me-state-card"
          data-state="searching"
          role="status"
        >
          <span className="near-me-card__icon" aria-hidden="true">
            <RefreshCw size={18} className="near-me-card__spin" />
          </span>
          <span className="near-me-card__msg">{t('home.nearMe.searching')}</span>
        </div>
        {Picker}
      </>
    );
  }

  // State B — nearby ads found.
  if (hasNearbyMatch) {
    // iter322 Phase A refinement — In manual-strict mode the matched
    // ads are bounded by the buyer's picked location, not GPS rings,
    // so the message focuses on the chosen label, not the km radius.
    let msg;
    if (isManualStrict && manualLabel) {
      msg = t('home.nearMe.strictModeShowing', { label: manualLabel });
    } else if (matchRadiusKm) {
      msg = t('home.nearMe.showingNearbyWithin', { km: matchRadiusKm });
    } else {
      msg = t('home.nearMe.showingNearby');
    }
    return (
      <>
        <div
          className="near-me-card near-me-card--success"
          data-testid="near-me-state-card"
          data-state="nearby-found"
          data-filter-mode={locationFilterMode || 'none'}
          data-match-radius={matchRadiusKm || ''}
          data-nearby-count={nearbyCount || 0}
          role="status"
        >
          <span className="near-me-card__icon" aria-hidden="true"><CheckCircle2 size={18} /></span>
          <span className="near-me-card__msg">{msg}</span>
          {manualLabel && !isManualStrict && (
            <span
              className="near-me-card__chosen"
              data-testid="near-me-location-chip"
              style={{ flex: '1 1 100%', fontSize: 12, opacity: 0.85, wordBreak: 'break-word' }}
            >
              {t('locationSelector.chosen')} <strong>{manualLabel}</strong>
            </span>
          )}
          <div className="near-me-card__actions">
            {locationStatus === 'granted' && !isManualStrict && (
              <button
                type="button"
                className="near-me-card__cta near-me-card__cta--ghost"
                data-testid="refresh-location-btn"
                onClick={onRequestLocation}
              >
                <RefreshCw size={13} />
                <span>{t('home.nearMe.refreshLocation')}</span>
              </button>
            )}
            <button
              type="button"
              className="near-me-card__cta near-me-card__cta--ghost"
              data-testid="choose-city-btn"
              onClick={onOpenPicker}
              aria-expanded={!!pickerOpen}
              aria-controls="near-me-location-picker"
            >
              <span>{isManualStrict ? t('home.nearMe.changeLocation') : t('home.nearMe.chooseCity')}</span>
            </button>
            {isManualStrict && (
              <button
                type="button"
                className="near-me-card__cta near-me-card__cta--ghost"
                data-testid="clear-location-btn"
                onClick={onClearLocation}
              >
                <span>{t('home.nearMe.clearLocation')}</span>
              </button>
            )}
          </div>
        </div>
        {Picker}
      </>
    );
  }

  // ─── States C/D/E ───
  // iter324 — Resolve title + buttons per a strict state machine so a
  // guest who never tapped "Use my location" NEVER sees the
  // permission-denied copy.
  //
  //   Priority (top wins):
  //     1. `denied`         → truly permission-blocked. Show "Location
  //                          access is off." + Try again / Choose city.
  //     2. `unsupported`    → browser has no geolocation API. Show
  //                          honest fallback message + Choose city.
  //     3. `error`          → GPS read failed (timeout / hardware).
  //                          Show retry + Choose city.
  //     4. `idle` + guest   → welcoming copy + 3 CTAs (GPS / city / sign in).
  //     5. `idle` + signed  → welcoming copy + 2 CTAs (GPS / city).
  //     6. `granted` but no nearby → "No ads very close to you yet..."
  //     7. else             → showingLiveFallback (catch-all).
  let primaryMsgKey = 'home.nearMe.showingLiveFallback';
  let stateName = 'no-location';
  let MsgIcon = MapPin;
  let cardTone = 'neutral';
  if (isDenied) {
    primaryMsgKey = 'home.nearMe.permissionDenied';
    stateName = 'permission-denied';
    MsgIcon = AlertCircle;
    cardTone = 'warn';
  } else if (isUnsupported) {
    primaryMsgKey = 'home.nearMe.locationError';
    stateName = 'location-unsupported';
    MsgIcon = AlertCircle;
    cardTone = 'warn';
  } else if (isError) {
    primaryMsgKey = 'home.nearMe.locationError';
    stateName = 'location-error';
    MsgIcon = AlertCircle;
    cardTone = 'warn';
  } else if (isIdle && isGuest) {
    primaryMsgKey = 'home.nearMe.guestIdleTitle';
    stateName = 'guest-idle';
    MsgIcon = MapPin;
    cardTone = 'neutral';
  } else if (isIdle) {
    primaryMsgKey = 'home.nearMe.userIdleTitle';
    stateName = 'user-idle';
    MsgIcon = MapPin;
    cardTone = 'neutral';
  } else if (hasLocation && !hasNearbyMatch) {
    primaryMsgKey = 'home.nearMe.noNearbyButLive';
    stateName = 'no-nearby-but-live';
    MsgIcon = MapPin;
    cardTone = 'neutral';
  }

  const isGranted = locationStatus === 'granted';
  // The location button still works in `denied` — a tap re-triggers
  // the browser prompt. We hide it only for truly unsupported.
  const showLocateCta = !isUnsupported;
  // Sign-in CTA — guest-only, suppressed for everything that already
  // has a clear action (denied → Try again wins; granted → not needed).
  const showSignInCta = isGuest && (isIdle || isError) && typeof onSignIn === 'function';
  // GPS button label varies by state so the user always knows what
  // pressing it will do.
  let locateLabel;
  if (isGranted) locateLabel = t('home.nearMe.refreshLocation');
  else if (isDenied || isError) locateLabel = t('home.nearMe.tryAgain');
  else locateLabel = t('home.nearMe.useMyLocation');
  // Manual city label: under a denial we explicitly say "Choose city
  // instead" so the user understands it's a fallback, not the primary
  // route. Otherwise we keep the welcoming "Choose your city".
  const cityLabel = (isDenied || isError)
    ? t('home.nearMe.chooseCityInstead')
    : t('home.nearMe.chooseCity');
  // Guest helper text — only after a granted GPS so the guest knows
  // they CAN sign in to save the preference. Never shown on the
  // initial guest-idle card because we already cover sign-in there.
  const showGuestGpsHelper = isGuest && isGranted && hasLocation && typeof onSignIn === 'function';

  return (
    <>
      <div
        className={`near-me-card near-me-card--${cardTone}`}
        data-testid="near-me-state-card"
        data-state={stateName}
        data-has-location={String(!!hasLocation)}
        data-has-nearby-match={String(!!hasNearbyMatch)}
        data-location-status={locationStatus}
        data-is-guest={String(!!isGuest)}
        role="note"
      >
        <span className="near-me-card__icon" aria-hidden="true"><MsgIcon size={18} /></span>
        <span className="near-me-card__msg">{t(primaryMsgKey)}</span>

        {manualLabel && (
          <span
            className="near-me-card__chosen"
            data-testid="near-me-location-chip"
            style={{ flex: '1 1 100%', fontSize: 12, opacity: 0.85, wordBreak: 'break-word' }}
          >
            {t('locationSelector.chosen')} <strong>{manualLabel}</strong>
          </span>
        )}

        {showGuestGpsHelper && (
          <span
            className="near-me-card__hint"
            data-testid="near-me-guest-gps-helper"
            style={{ flex: '1 1 100%', fontSize: 12, opacity: 0.75 }}
          >
            {t('home.nearMe.guestGpsHelper')}
          </span>
        )}

        <div className="near-me-card__actions">
          {showLocateCta && (
            <button
              type="button"
              className="near-me-card__cta"
              data-testid="use-my-location-btn"
              onClick={onRequestLocation}
              disabled={isSearching}
            >
              <MapPin size={13} />
              <span>{locateLabel}</span>
            </button>
          )}
          <button
            type="button"
            className="near-me-card__cta near-me-card__cta--ghost"
            data-testid="choose-city-btn"
            onClick={onOpenPicker}
            aria-expanded={!!pickerOpen}
            aria-controls="near-me-location-picker"
          >
            <span>{cityLabel}</span>
          </button>
          {showSignInCta && (
            <button
              type="button"
              className="near-me-card__cta near-me-card__cta--ghost"
              data-testid="near-me-sign-in-btn"
              onClick={onSignIn}
            >
              <span>{t('home.nearMe.signIn')}</span>
            </button>
          )}
        </div>
      </div>
      {Picker}
    </>
  );
}
